import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import {
  loadSituations,
  loadActivities,
  type Situation,
  type Activity,
} from '@/services/content'
import Icon from '@/components/ui/Icon'
import DailyTips from '@/components/home/DailyTips'
import styles from './Home.module.css'

export default function Home() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const childName = user?.child_profile?.name
  const [situations, setSituations] = useState<Situation[]>([])
  const [quickWins, setQuickWins] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([loadSituations(), loadActivities()])
      .then(([sits, acts]) => {
        setSituations(sits)
        const quick = acts.filter(a => !a.isPremium && a.duration <= 5).slice(0, 4)
        setQuickWins(quick)
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>
          {childName ? `Hi, ${childName}'s parent` : 'Welcome to MelloMap'}
        </h1>
        <p className={styles.heroSubtitle}>
          Your child isn't difficult. Their behavior is telling you something. Let's decode it together.
        </p>
      </section>

      {/* Three Pillars */}
      <section className={styles.pillars}>
        <button className={styles.pillarCard} onClick={() => navigate('/library')}>
          <div className={styles.pillarIcon}><Icon name="life-buoy" size={28} /></div>
          <div className={styles.pillarText}>
            <h3 className={styles.pillarTitle}>Help me right now</h3>
            <p className={styles.pillarDesc}>Activities for what's happening today</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>

        <button className={styles.pillarCard} onClick={() => navigate('/programs')}>
          <div className={styles.pillarIcon}><Icon name="calendar" size={28} /></div>
          <div className={styles.pillarText}>
            <h3 className={styles.pillarTitle}>Help me this week</h3>
            <p className={styles.pillarDesc}>Guided multi-day programs</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>

        <button className={styles.pillarCard} onClick={() => navigate('/my-child')}>
          <div className={styles.pillarIcon}><Icon name="lightbulb" size={28} /></div>
          <div className={styles.pillarText}>
            <h3 className={styles.pillarTitle}>Help me understand</h3>
            <p className={styles.pillarDesc}>Why does my child do this?</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>
      </section>

      {/* Ask MelloMap — AI Chat */}
      <section>
        <button className={styles.askCard} onClick={() => navigate('/ask')}>
          <span className={styles.askEmoji}><Icon name="message-circle" size={20} /></span>
          <div className={styles.askText}>
            <h3 className={styles.askTitle}>Ask MelloMap</h3>
            <p className={styles.askDesc}>Type any question about your child's behavior</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>
      </section>

      {/* What's going on? — Situations */}
      <section className={styles.situations}>
        <h2 className={styles.sectionTitle}>What's going on?</h2>
        <p className={styles.sectionSubtitle}>Tap what fits — we'll show you what to do</p>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <div className={styles.situationGrid}>
            {situations.map((situation) => (
              <button
                key={situation.id}
                className={styles.situationCard}
                onClick={() => navigate(`/situation/${situation.id}`)}
              >
                <span className={styles.situationIcon}>
                  <Icon name={situation.icon} size={28} />
                </span>
                <span className={styles.situationLabel}>{situation.title}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Tools & Trackers */}
      <section>
        <h2 className={styles.sectionTitle}>Tools & Trackers</h2>
        <p className={styles.sectionSubtitle}>Track patterns, build routines, understand your child</p>
        <div className={styles.toolsGrid}>
          <button className={styles.toolCard} onClick={() => navigate('/check-in')}>
            <span className={styles.toolEmoji}><Icon name="smile" size={22} /></span>
            <span className={styles.toolLabel}>Emotion Check-in</span>
          </button>
          <button className={styles.toolCard} onClick={() => navigate('/journal')}>
            <span className={styles.toolEmoji}><Icon name="notebook" size={22} /></span>
            <span className={styles.toolLabel}>Behavior Journal</span>
          </button>
          <button className={styles.toolCard} onClick={() => navigate('/routines')}>
            <span className={styles.toolEmoji}><Icon name="clipboard-list" size={22} /></span>
            <span className={styles.toolLabel}>Daily Routines</span>
          </button>
          <button className={styles.toolCard} onClick={() => navigate('/food-tracker')}>
            <span className={styles.toolEmoji}><Icon name="salad" size={22} /></span>
            <span className={styles.toolLabel}>Food Tracker</span>
          </button>
          <button className={styles.toolCard} onClick={() => navigate('/milestones')}>
            <span className={styles.toolEmoji}><Icon name="bar-chart-3" size={22} /></span>
            <span className={styles.toolLabel}>Milestones</span>
          </button>
          <button className={styles.toolCard} onClick={() => navigate('/school-readiness')}>
            <span className={styles.toolEmoji}><Icon name="graduation-cap" size={22} /></span>
            <span className={styles.toolLabel}>School Ready?</span>
          </button>
        </div>
      </section>

      {/* Quick Wins */}
      {quickWins.length > 0 && (
        <section className={styles.quickWins}>
          <h2 className={styles.sectionTitle}>Need something quick?</h2>
          <p className={styles.sectionSubtitle}>Under 5 minutes, no prep needed</p>
          <div className={styles.quickWinList}>
            {quickWins.map((activity) => (
              <button
                key={activity.id}
                className={styles.quickWinCard}
                onClick={() => navigate(`/activity/${activity.id}`)}
              >
                <div className={styles.quickWinTop}>
                  <span className={styles.quickWinBadge}>{activity.duration} min</span>
                </div>
                <span className={styles.quickWinTitle}>{activity.title}</span>
                <span className={styles.quickWinDesc}>{activity.whenToUse}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Daily Tip */}
      <DailyTips />

      {/* Bottom Cards */}
      <section className={styles.bottomCards}>
        <button className={styles.exploreCard} onClick={() => navigate('/self-care')}>
          <span className={styles.exploreIcon}><Icon name="flower-2" size={22} /></span>
          <div className={styles.exploreText}>
            <h3 className={styles.exploreTitle}>Your Calm Corner</h3>
            <p className={styles.exploreDesc}>Breathing, affirmations & quick resets for you</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>

        <button className={styles.exploreCard} onClick={() => navigate('/videos')}>
          <span className={styles.exploreIcon}><Icon name="clapperboard" size={22} /></span>
          <div className={styles.exploreText}>
            <h3 className={styles.exploreTitle}>Watch & Learn</h3>
            <p className={styles.exploreDesc}>Short videos on calming, movement & mealtime</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>

        <button className={styles.exploreCard} onClick={() => navigate('/library')}>
          <span className={styles.exploreIcon}><Icon name="library" size={22} /></span>
          <div className={styles.exploreText}>
            <h3 className={styles.exploreTitle}>Browse full library</h3>
            <p className={styles.exploreDesc}>100+ activities, printables, books & toys</p>
          </div>
          <span className={styles.pillarArrow}>›</span>
        </button>
      </section>
    </div>
  )
}
